<template>
  <AdvancedSelect
    v-model="content"
    :id="id"
    searchable
    :value-field="valueField"
    :text-field="textField"
    :selected-text-field="selectedTextField"
    :size="size"
    :items="options"
    :multiple="multiple"
    :has-error="hasError"
    :disabled="disabled"
    :dropdown-direction="dropdownDirection"
    :dropdown-wrapper-class="dropdownWrapperClass"
    :control-class="controlClass"
    :auto-adjust-values-to-items="false"
    :close-on-select="closeOnSelect"
    :custom-search-handler="(searchText, items) => items"
    :tagDisplayLimit="tagDisplayLimit"
    :comparisonMode="comparisonMode"
    @input="selectedItemChanged"
    @select="$emit('select', $event)"
    @deselect="$emit('deselect', $event)"
    @change="$emit('change', $event)"
    @dropdownShow="$emit('dropdownShow', $event)"
    @dropdownHide="$emit('dropdownHide', $event)"
    @searchTextChange="handleSearch(true, $event)"
    @reset="$emit('reset', $event)"
    @comparisonModeChange="$emit('comparisonModeChange', $event)"
    :isLoadingOption="isFetchingOptions"
    :getItemClass="getItemClass"
    :inputClass="getItemClass(content)"
  />
</template>

<script>
import AdvancedSelect from '../advancedselect/AdvancedSelect.vue';

export default {
  name: 'AdvancedSelectWithPath',

  emits: ['select', 'deselect', 'input', 'change', 'dropdownShow', 'dropdownHide', 'searchTextChange', 'optionChanged'],

  components: {
    AdvancedSelect
  },

  props: {
    id: String,
    value: {
      type: [String, Number, Array]
    },
    valueField: {
      type: String,
      default: 'id'
    },
    textField: {
      type: String,
      default: 'name'
    },
    selectedTextField: {
      type: String,
      default: null
    },
    multiple: {
      type: Boolean,
      default: false
    },
    closeOnSelect: {
      type: Boolean,
      default: true
    },
    showFetchingStatus: {
      type: Boolean,
      default: true
    },
    path: {
      type: String,
      default: ''
    },
    pathParams: {
      type: Object,
      default () {
        return {};
      }
    },
    hasGetParams: {
      type: Boolean,
      default: false
    },
    dropdownDirection: String,
    dropdownWrapperClass: {
      type: [String, Array, Object]
    },
    controlClass: {
      type: [String, Array, Object]
    },
    disabled: Boolean,
    hasError: {
      type: Boolean,
      default: false
    },
    fetchLimit: {
      type: Number,
      default: 20
    },
    size: String,
    responseParam: {
      type: String,
      default: null
    },
    /** Prevent auto searching on mounting with existing value */
    loadsearch: {
      type: Boolean,
      default: true
    },
    searchParam: {
      type: String,
      default: 'search_text'
    },
    hasToken: {
      // Set to false if use other resource without Bearer token
      type: Boolean,
      default: true
    },
    translate: {
      type: Boolean,
      default: false
    },
    searchDelay: {
      type: Number,
      default: 0
    },
    textval: {
      type: String,
      default: null
    },
    getItemClass: {
      type: Function,
      default: (item) => ''
    },
    tagDisplayLimit: {
      type: Number,
      default: 5
    },
    comparisonMode: {
      type: String,
      default: ''
    }
  },

  data () {
    return {
      content: null,
      options: [],
      selected: [],
      currentFilterRequest: null,
      isFetchingOptions: false,
      currentSearchText: ''
    }
  },

  methods: {
    async handleSearch (emitTextChange = false, text = null) {
      let skipSearch = false;
      if (!this.multiple && text == this.content && !this.loadsearch && text != '') {
        skipSearch = true;
      }
      if (emitTextChange) {
        this.$emit('searchTextChange', text);
      }
      if (!this.path || skipSearch) {
        return;
      }
      this.currentSearchText = text;
      let path = '';
      const pathParams = this.BasicHelper.cloneObject(this.pathParams);
      let delay = this.searchDelay;
      let searchText = text;
      if (!this.multiple && this.value) {
        pathParams[this.valueField] = this.value;
        searchText = null;
        delay = 0;
      } else {
        pathParams.limit = this.fetchLimit;
      }
      path = this.ApiRequest.addGetParamsToPath(this.path, pathParams, this.hasGetParams);
      const { data } = await this.fetchOptions(path, delay, searchText);
      const options = (this.responseParam ? data[this.responseParam] : data);
      if (this.translate) {
        this.options = options.map(option => {
          option[this.textField] = this.BasicHelper.getTranslation(option[this.textField]);
          return option;
        })
      }
      this.options = options;

      if (this.content !== this.value) {
        this.content = this.value;
      }
    },

    fetchOptions (path, delay = 0, searchText = null) {
      try {
        return new Promise((resolve, reject) => {
          if (this.showFetchingStatus) {
            this.isFetchingOptions = true;
          }
          setTimeout(() => {
            if (searchText == null || this.currentSearchText == searchText) {
              if (searchText !== null) {
                const pathParams = {};
                pathParams[this.searchParam] = searchText;
                path = this.ApiRequest.addGetParamsToPath(path, pathParams, this.hasGetParams);
              }
              if (this.currentFilterRequest) {
                this.currentFilterRequest.cancel('changed search query');
                this.currentFilterRequest = null;
              }
              this.currentFilterRequest = this.ApiRequest.request(path, this.ApiRequest.REQUEST_GET, [], response => {
                if (response.error) {
                  this.ApiRequest.displayErrorDialog(response, this);
                  reject(response.error);
                }
                if (this.showFetchingStatus) {
                  this.isFetchingOptions = false;
                }
                resolve(response);
              }, false, this.hasToken);
            }
          }, delay);
        });
      } catch (e) {
        console.error(e);
      }
    },
    setContentOption (val, textval = null) {
      const option = {};
      option[this.valueField] = val;
      if (textval == null) {
        option[this.textField] = val;
      } else {
        option[this.textField] = textval;
      }
      this.options = [option];
    },

    selectedItemChanged (val) {
      let selectedItem = null;
      if (val !== null && this.options) {
        selectedItem = this.options.find(el => el[this.valueField] == val);
      }
      this.$emit('input', val);
      this.$emit('optionChanged', val, selectedItem);
    }
  },

  watch: {
    value (val) {
      if (this.loadsearch) {
        this.handleSearch();
      } else if (this.content !== val) {
        this.setContentOption(val, this.textval);
        this.content = val;
      }
    }
  },

  mounted () {
    if (this.loadsearch) {
      this.handleSearch();
    } else if (this.content !== this.value) {
      this.setContentOption(this.value, this.textval);
      this.content = this.value;
    }
  }
}
</script>
